import React, {useMemo, useState} from 'react';
import BuildingDocumentsCard from '../building/BuildingDocumentsCard';
import {DocumentUploadModal} from "./component/document/DocumentUploadModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faFileDownload} from "@fortawesome/free-solid-svg-icons";
import {API_ROOT} from "../../config";
import {formatDateToIso} from "../../DemandPointContext";

const BUILDING_DOCUMENT_TYPES = {
  'bauakte': 'Bauakte (Materialbedarf)',
  'faserzuteilungsplan': 'Faserzuteilungsplan',
  'auskundungsprotokoll': 'Auskundungsprotokoll'
};

const UNIT_DOCUMENTATION_TYPES_FOR_CONTENT_CARDS = {
  DOCUMENT_INSTALL_PROTOCOL: 'Installationsprotokoll',
  OTHER: 'Gebäude-Dokumente'
}

const DocumentationContent = ({ unit, building, unitDocuments, setUnitDocuments }) => {
  const [uploadModal, setUploadModal] = useState(false);
  const [currentDocumentType, setCurrentDocumentType] = useState('');
  
  const startDocumentUpload = (unitDocType) => {
    setCurrentDocumentType(unitDocType);
    setUploadModal(true);
  };

  const onFilesUpload = async (files) => {

    if (!files || files.length === 0) return;
    const file = files[0];

    // Basic client-side checks
    if (file.size > 5 * 1024 * 1024) {
      alert('Datei ist zu groß (max. 5 MB)');
      return;
    }

    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (allowed.indexOf(file.type) === -1) {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        alert('Nur PDF, JPG, PNG und Word Dateien sind erlaubt.');
        return;
      }
    }

    // Build FormData and POST to API
    const formDataUpload = new FormData();
    formDataUpload.append('documentType', 'UNIT');
    formDataUpload.append('displayName', file.name);
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(`${API_ROOT}/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const newDoc = await response.json();

      const normalizedDoc = {
        tempId: newDoc.id || crypto.randomUUID(),
        externalId: null,
        externalDocumentId: newDoc.externalDocumentId || newDoc.externalId,
        displayName: newDoc.displayName || file.name,
        fileSize: newDoc.fileSize || file.size,
        documentType: 'UNIT',
        dateOfCreation: newDoc.dateOfCreation ? formatDateToIso(newDoc.dateOfCreation) : new Date().toISOString(),
        lastModificationDate: newDoc.lastModificationDate ? formatDateToIso(newDoc.lastModificationDate) : new Date().toISOString(),
        version: 0,
        tags: newDoc.tags || [],
        unitDocumentType: currentDocumentType,
        isTemp: true,
      };

      setUnitDocuments((prev) => [...prev, normalizedDoc]);

      setUploadModal(false);
      setCurrentDocumentType('');

    } catch (err) {
      console.error('Error uploading building document:', err);
      alert('Fehler beim Hochladen des Dokuments');
    }
  };

  const removeDocument = (tempIdToRemove) => {
    setUnitDocuments(prev => prev.filter(doc => doc.tempId !== tempIdToRemove));
  };

  const getDocumentUrl = (objectName, loadThumbnail = false, isTemp = false) => {
    return `${API_ROOT}/document?documentType=${isTemp ? "TEMP": "UNIT"}&objectName=${encodeURIComponent(objectName)}&loadThumbnail=${loadThumbnail}`;
  };

  const downloadDocument = async (doc) => {
    try {
      const objectName = doc.externalDocumentId;
      const displayName = doc.displayName;
      const url = getDocumentUrl(objectName, false, !!doc.isTemp);
      const token = localStorage.getItem('fastplanet_token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayName || objectName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Fehler beim Herunterladen des Dokuments');
    }
  };

  return (
    <div className="space-y-6">
      <BuildingDocumentsCard building={building} buildingDocumentTypes={BUILDING_DOCUMENT_TYPES} />
      
      <div>
        <h6 className="text-sm font-medium text-gray-700 mb-3">Einheit-Dokumentation</h6>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Unit Document Types (excluding fotos_bauausfuehrung which is now in Photos tab) */}
        {Object.entries(UNIT_DOCUMENTATION_TYPES_FOR_CONTENT_CARDS).map(([key, label]) => {
          let filteredDocuments = unitDocuments.filter(doc => doc.unitDocumentType === key);

          return (
            <div key={key} className="border rounded-lg p-3 bg-white">
              <h6 className="text-sm font-medium text-gray-700 mb-2">{label}</h6>
              <div className="space-y-2">
                {filteredDocuments.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                    <span className="truncate flex-1">{doc.displayName}</span>
                    <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        title="Foto bearbeiten"
                        className={"mr-3"}
                    >
                      <div className="flex flex-col items-center justify-center text-orange-400">
                        <FontAwesomeIcon icon={faFileDownload} size="1x" />
                      </div>
                    </button>
                    <button
                        type="button"
                        onClick={() => removeDocument(doc.tempId)}
                        title="Foto löschen"
                    >
                      <div className="flex flex-col items-center justify-center text-orange-400">
                        <FontAwesomeIcon icon={faClose} size="1x" />
                      </div>
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => startDocumentUpload(key)}
                  className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors text-sm"
                >
                  + Dokument hinzufügen
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* No documents message */}
      {unitDocuments.filter(doc => Object.keys(UNIT_DOCUMENTATION_TYPES_FOR_CONTENT_CARDS).includes(doc.unitDocumentType)).length  === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">Noch keine Dokumente vorhanden</p>
          <p className="text-xs mt-1">Verwenden Sie die Upload-Buttons oben um Dokumente hinzuzufügen</p>
        </div>
      )}

      {/* File Upload Modal */}
      {uploadModal && (
          <DocumentUploadModal
              onClose={() => setUploadModal(false)}
              textDocumentType={UNIT_DOCUMENTATION_TYPES_FOR_CONTENT_CARDS[currentDocumentType]}
              onFilesUpload={onFilesUpload}
          />
      )}
      </div>
    </div>
  );
};

export default DocumentationContent;
